import { PageType } from '@app-types/PageType'
import { useRouter } from 'next/router'
import { type RefObject, useCallback, useEffect, useRef, useState } from 'react'

const DEFAULT_DURATION = 120 // 120 seconds per page default

/** Keeps automatic scrolling enabled across page changes and content loading. */
export const useAutoScroll = (
  type: PageType,
  scrollContainerRef?: RefObject<HTMLElement | null>,
  useScrollContainer = false,
  isContentReady = true,
) => {
  const router = useRouter()
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(false)
  const [isActivelyScrolling, setIsActivelyScrolling] = useState(false)

  // Store duration in seconds (time to read one page)
  const [duration, setDurationSTATE] = useState(DEFAULT_DURATION)

  const requestRef = useRef<number | undefined>(undefined)
  const lastTimeRef = useRef<number | undefined>(undefined)
  const scrollAccumulatorRef = useRef<number>(0)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Track continuous user interaction to pause scrolling
  const isInteractingRef = useRef<boolean>(false)
  const interactionTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const [isPending, setIsPending] = useState(false)

  const getScrollContainer = useCallback(
    () => (useScrollContainer ? scrollContainerRef?.current || null : null),
    [scrollContainerRef, useScrollContainer],
  )

  // Initialize duration from localStorage on mount
  useEffect(() => {
    const savedDuration = localStorage.getItem(`autoScrollDuration_${type}`)
    if (savedDuration) {
      setDurationSTATE(Number(savedDuration))
    }
  }, [type])

  const setDuration = useCallback(
    (newDuration: number) => {
      setDurationSTATE(newDuration)
      localStorage.setItem(`autoScrollDuration_${type}`, String(newDuration))
    },
    [type],
  )

  const toggleScroll = useCallback(() => {
    setIsAutoScrollEnabled((prev) => {
      const isNowEnabled = !prev
      if (!isNowEnabled) {
        // Stopping
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
          timeoutRef.current = null
        }
        setIsActivelyScrolling(false)
        setIsPending(false)
      } else {
        // Starting
        const scrollContainer = getScrollContainer()
        const scrollTop = scrollContainer?.scrollTop ?? window.scrollY

        if (scrollTop === 0) {
          setIsPending(true)
          setIsActivelyScrolling(false)
          timeoutRef.current = window.setTimeout(() => {
            setIsActivelyScrolling(true)
            setIsPending(false)
            timeoutRef.current = null
          }, 3000) as unknown as NodeJS.Timeout
        } else {
          setIsActivelyScrolling(true)
        }
      }
      return isNowEnabled
    })
  }, [getScrollContainer])

  // Listen to route changes
  const prevPathRef = useRef(router.asPath)
  useEffect(() => {
    if (prevPathRef.current !== router.asPath) {
      prevPathRef.current = router.asPath
      if (isAutoScrollEnabled) {
        setIsActivelyScrolling(false)
        setIsPending(true)
        if (timeoutRef.current) clearTimeout(timeoutRef.current)
        timeoutRef.current = setTimeout(() => {
          setIsActivelyScrolling(true)
          setIsPending(false)
          timeoutRef.current = null
        }, 3000) as unknown as NodeJS.Timeout
      }
    }
  }, [router.asPath, isAutoScrollEnabled])

  useEffect(() => {
    // A native bridge may still be loading after the route's start delay.
    // Keep the enabled state, but do not scroll or detect the end of a placeholder.
    if (!isActivelyScrolling || !isContentReady) {
      lastTimeRef.current = undefined
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current)
      }
      return
    }

    const step = (time: number) => {
      if (lastTimeRef.current !== undefined) {
        const deltaTime = time - lastTimeRef.current

        if (!isInteractingRef.current) {
          const scrollContainer = getScrollContainer()
          const scrollHeight =
            scrollContainer?.scrollHeight ?? document.documentElement.scrollHeight
          const clientHeight =
            scrollContainer?.clientHeight ?? document.documentElement.clientHeight
          const scrollTop = scrollContainer?.scrollTop ?? window.scrollY
          const scrollableDistance = scrollHeight - clientHeight

          // Stay active at the bottom so scrolling resumes when the next page
          // resets the viewport, even if its content arrives after the route delay.
          const isAtPageEnd = clientHeight + scrollTop >= scrollHeight - 1
          if (isAtPageEnd) {
            scrollAccumulatorRef.current = 0
          }

          if (scrollableDistance > 0 && !isAtPageEnd) {
            const speed = scrollableDistance / (duration * 1000)
            const scrollAmount = speed * deltaTime
            scrollAccumulatorRef.current += scrollAmount

            if (scrollAccumulatorRef.current >= 1) {
              const pixelsToScroll = Math.floor(scrollAccumulatorRef.current)
              if (scrollContainer) {
                scrollContainer.scrollBy(0, pixelsToScroll)
              } else {
                window.scrollBy(0, pixelsToScroll)
              }
              scrollAccumulatorRef.current -= pixelsToScroll
            }
          }
        }
      }
      lastTimeRef.current = time
      requestRef.current = requestAnimationFrame(step)
    }

    requestRef.current = requestAnimationFrame(step)

    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current)
      }
    }
  }, [isActivelyScrolling, isContentReady, duration, getScrollContainer])

  // Manage pause/resume on user interaction
  useEffect(() => {
    if (!isActivelyScrolling && !isPending) return

    const pauseInteraction = () => {
      isInteractingRef.current = true
      if (interactionTimeoutRef.current) {
        clearTimeout(interactionTimeoutRef.current)
      }
    }

    const resumeInteraction = () => {
      isInteractingRef.current = false
    }

    const handleTemporaryInteraction = () => {
      pauseInteraction()
      // Resume after a short delay of no scrolling/moving (1.5 seconds)
      interactionTimeoutRef.current = setTimeout(
        resumeInteraction,
        1500,
      ) as unknown as NodeJS.Timeout
    }

    // Explicit pointer/touch down means we pause until they release
    window.addEventListener('mousedown', pauseInteraction, { passive: true })
    window.addEventListener('touchstart', pauseInteraction, { passive: true })

    // Explicit pointer/touch up/leave means we resume
    window.addEventListener('mouseup', resumeInteraction, { passive: true })
    window.addEventListener('mouseleave', resumeInteraction, { passive: true })
    window.addEventListener('touchend', resumeInteraction, { passive: true })
    window.addEventListener('touchcancel', resumeInteraction, { passive: true })

    // Continuous interactions (wheel, keydown, touchmove) cause a temporary pause
    window.addEventListener('wheel', handleTemporaryInteraction, { passive: true })
    window.addEventListener('touchmove', handleTemporaryInteraction, { passive: true })
    window.addEventListener('keydown', handleTemporaryInteraction, { passive: true })

    return () => {
      if (interactionTimeoutRef.current) {
        clearTimeout(interactionTimeoutRef.current)
        interactionTimeoutRef.current = null
      }
      // The cancelled resume timer must not leave the next page paused forever.
      isInteractingRef.current = false
      window.removeEventListener('mousedown', pauseInteraction)
      window.removeEventListener('touchstart', pauseInteraction)
      window.removeEventListener('mouseup', resumeInteraction)
      window.removeEventListener('mouseleave', resumeInteraction)
      window.removeEventListener('touchend', resumeInteraction)
      window.removeEventListener('touchcancel', resumeInteraction)

      window.removeEventListener('wheel', handleTemporaryInteraction)
      window.removeEventListener('touchmove', handleTemporaryInteraction)
      window.removeEventListener('keydown', handleTemporaryInteraction)
    }
  }, [isActivelyScrolling, isPending])

  return {
    isScrolling: isAutoScrollEnabled,
    isPending,
    toggleScroll,
    duration,
    setDuration,
  }
}

export default useAutoScroll
